import { Fragment } from 'react';
import { ArrowDown, ArrowRight, Hand } from 'lucide-react';
import LiquidWord from './LiquidWord';
import WaterRipple from './WaterRipple';
import { company, heroImage, products, technologies } from '../data/content';

export function Ticker() {
  const items = [...technologies, ...technologies];
  return (
    <div className="relative z-20 -mt-8 -rotate-[1.5deg] overflow-hidden bg-brand-600 py-4 shadow-xl">
      <div className="marquee-track flex w-max animate-marquee items-center gap-8 whitespace-nowrap">
        {items.map((t, i) => (
          <span key={i} className="flex items-center gap-8 font-display text-lg font-semibold uppercase tracking-wider text-white sm:text-xl">
            {t}
            <span className="h-2.5 w-2.5 rotate-45 bg-white/70" />
          </span>
        ))}
      </div>
    </div>
  );
}

// WTP → STP → ETP → GWT → ZLD, drawn as a short pipeline of stage links.
function ProductPipeline() {
  return (
    <nav aria-label="Products" className="flex flex-wrap items-center gap-y-3">
      {products.map((p, i) => (
        <Fragment key={p.id}>
          {i > 0 && (
            <span className="relative mx-1 h-0.5 w-5 overflow-hidden bg-white/15 sm:w-8" aria-hidden="true">
              <span className="absolute inset-y-0 left-0 w-1/2 animate-[pipeflow_1.4s_linear_infinite] bg-brand-500" />
            </span>
          )}
          <a
            href={`#product-${p.id}`}
            title={p.name}
            className="rounded-full bg-white/5 px-3.5 py-2 font-display text-sm font-semibold tracking-wider text-white ring-1 ring-white/20 backdrop-blur transition hover:bg-brand-600 hover:ring-brand-600"
          >
            {p.code}
          </a>
        </Fragment>
      ))}
    </nav>
  );
}

export default function Hero() {
  return (
    <section
      id="home"
      className="relative flex min-h-[100svh] touch-pan-y flex-col overflow-hidden bg-ink text-white"
    >
      <WaterRipple src={`${process.env.PUBLIC_URL}${heroImage}`} className="absolute inset-0 h-full w-full" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/10 to-ink" />
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-50" />

      <div className="container-x relative flex flex-1 flex-col justify-end pb-24 pt-32 lg:pb-28">
        <div className="reveal flex flex-wrap items-center justify-between gap-4">
          <span className="inline-flex items-center gap-3 rounded-full bg-ink/60 py-1.5 pl-1.5 pr-4 text-xs font-medium text-gray-300 ring-1 ring-white/10 backdrop-blur">
            <span className="rounded-full bg-brand-600 px-2.5 py-1 font-semibold text-white">Ahmedabad · India</span>
            Water &amp; wastewater treatment specialists
          </span>
          <span className="hidden items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-white/60 md:inline-flex">
            <Hand className="h-4 w-4 animate-float text-brand-400" /> Touch the water
          </span>
        </div>

        <LiquidWord text="AGNISAFE" className="reveal mt-8 w-full drop-shadow-[0_20px_50px_rgba(208,33,41,0.35)]" />
        <div className="reveal -mt-1 flex items-center gap-4 text-[0.65rem] font-semibold uppercase tracking-[0.5em] text-white/60 sm:text-xs">
          <span className="h-px flex-1 bg-white/20" />
          Engineering LLP
          <span className="h-px flex-1 bg-white/20" />
        </div>

        <div className="reveal mt-12 grid gap-10 lg:grid-cols-12 lg:items-end">
          <h1 className="text-4xl font-bold leading-[1.02] sm:text-5xl lg:col-span-5 xl:text-6xl">
            Pure water.
            <br />
            <span className="text-brand-500">Sustainable</span> tomorrow.
          </h1>

          <div className="lg:col-span-4">
            <p className="text-base leading-relaxed text-gray-300">
              <span className="font-semibold text-white">{company.name}</span> designs, builds and
              operates drinking water, wastewater treatment and water reuse plants for the municipal
              and industrial sectors — from KLD to MLD capacity.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 lg:col-span-3 lg:justify-end">
            <a href="#contact" className="btn-primary !px-7 !py-4">
              Get a Quote <ArrowRight className="h-4 w-4" />
            </a>
            <a href="#about" className="btn-ghost !py-4 backdrop-blur" aria-label="Scroll to About">
              <ArrowDown className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="reveal mt-12 flex flex-col gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-white/50">Our treatment line</span>
          <ProductPipeline />
        </div>
      </div>
    </section>
  );
}
